import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message, Card, Space } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './ChangePasswordPage.css';

const ChangePasswordPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const onFinish = async (values: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    try {
      setLoading(true);
      
      // 验证新密码和确认密码是否一致
      if (values.newPassword !== values.confirmPassword) {
        message.error('两次输入的新密码不一致');
        return;
      }

      // 调用API修改密码
      await authAPI.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });

      message.success('密码修改成功，请重新登录');
      
      // 退出登录并跳转到登录页
      logout();
      navigate('/login');
    } catch (error: any) {
      message.error(error.response?.data?.message || '密码修改失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/my/profile');
  };

  return (
    <div className="change-password-page">
      <div className="page-header">
        <h1>修改密码</h1>
      </div>
      
      <div className="content-container">
        <Card className="password-card">
          <Form
            form={form}
            name="changePassword"
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="currentPassword"
              label="当前密码"
              rules={[
                { required: true, message: '请输入当前密码' },
                { min: 6, message: '密码长度至少为6位' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入当前密码"
                maxLength={50}
              />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label="新密码"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '密码长度至少为6位' },
                {
                  validator: (_, value) => {
                    if (value && value === form.getFieldValue('currentPassword')) {
                      return Promise.reject(new Error('新密码不能与当前密码相同'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入新密码"
                maxLength={50}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="确认新密码"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '请再次输入新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请再次输入新密码"
                maxLength={50}
              />
            </Form.Item>

            <Form.Item>
              <Space size="large" style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={handleCancel}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  确认修改
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <div className="password-tips">
          <h3>密码设置建议：</h3>
          <ul>
            <li>密码长度至少为6位</li>
            <li>建议使用字母、数字和特殊字符的组合</li>
            <li>不要使用过于简单的密码，如123456</li>
            <li>定期更换密码可以提高账户安全性</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;